/**
 * Alert Component (Toaster/Toast)
 *
 * A flexible alert/notification component based on the Figma design system.
 * Supports multiple variants: floating and full-width, with different color schemes.
 *
 * @example
 * // Floating alert (default)
 * <Alert
 *   title="We've just released a new feature"
 *   description="Exciting news! Check it out now!"
 *   color="default"
 * />
 *
 * @example
 * // Full-width alert
 * <Alert
 *   title="Important notice"
 *   description="Please review the changes"
 *   size="full-width"
 *   color="warning"
 * />
 *
 * @example
 * // With actions
 * <Alert
 *   title="New update available"
 *   description="Version 2.0 is here!"
 *   color="success"
 *   primaryAction={{ label: "Update now", onClick: () => {} }}
 *   secondaryAction={{ label: "Later", onClick: () => {} }}
 * />
 */
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from 'src/utils/utils';
import { Button } from './button';
import { Icon } from './icon';
import { Typography } from './typography';

// Import icons - you'll need to have these SVG files
import { ReactComponent as CheckCircleIcon } from 'src/assets/tailwind/check-circle.svg';
import { ReactComponent as HelpCircleIcon } from 'src/assets/tailwind/help-circle.svg';
import { ReactComponent as InfoCircleIcon } from 'src/assets/tailwind/info-circle.svg';
import { ReactComponent as XCircleIcon } from 'src/assets/tailwind/x-circle.svg';
import { ReactComponent as CloseIcon } from 'src/assets/tailwind/x.svg';

// ============================================
// VARIANT DEFINITIONS
// ============================================

const alertVariants = cva(
  // Base styles common to all variants
  'tw:flex tw:gap-l tw:p-l tw:rounded-bw-2xl-3xl tw:border tw:border-solid',
  {
    variants: {
      /**
       * Size variant:
       * - floating: Fixed width (380px mobile, 520px desktop), with shadow
       * - full-width: Spans full container width, no border radius
       *
       * Note: We use mobile-first approach:
       * - Base styles (no prefix) apply to all sizes (including mobile)
       * - tablet: applies at 481px+
       * - desktop: applies at 769px+
       */
      size: {
        floating: `
          tw:w-max

          tw:shadow-e1
        `,
        'full-width': `
          tw:w-max

          tw:p-l
          tw:table:py-m
          tw:tablet:px-3xl

          tw:rounded-none
        `
      },
      /**
       * Color variant determines the visual style
       */
      color: {
        default: '',
        brand: '',
        error: '',
        warning: '',
        success: '',
        info: ''
      },
      /**
       * Type variant:
       * - default: White background with colored border
       * - colored: Colored background
       */
      type: {
        default: 'tw:bg-primary tw:border-border-primary',
        colored: ''
      }
    },
    compoundVariants: [
      // Default color + colored type
      {
        color: 'default',
        type: 'colored',
        className: 'tw:bg-secondary tw:border-border-gray-dark'
      },
      // Brand color + default type
      {
        color: 'brand',
        type: 'colored',
        className: 'tw:bg-brand-light tw:border-border-brand'
      },
      // Info color + colored type
      {
        color: 'info',
        type: 'colored',
        className: 'tw:bg-info tw:border-border-info'
      },
      // Error color + colored type
      {
        color: 'error',
        type: 'colored',
        className: 'tw:bg-error tw:border-border-error'
      },
      // Warning color + colored type
      {
        color: 'warning',
        type: 'colored',
        className: 'tw:bg-warning tw:border-border-warning'
      },
      // Success color + colored type
      {
        color: 'success',
        type: 'colored',
        className: 'tw:bg-success tw:border-border-success'
      },
      // Full-width adjustments
      {
        size: 'full-width',
        className:
          'tw:border-x-0 tw:mobile:border-b-0 tw:tablet:border-b tw:tablet:border-t-0'
      }
    ],
    defaultVariants: {
      size: 'floating',
      color: 'default',
      type: 'default'
    }
  }
);

// ============================================
// TYPES
// ============================================

export interface AlertAction {
  label: string;
  onClick: () => void;
}

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'color'>,
    VariantProps<typeof alertVariants> {
  /**
   * Alert title text
   */
  title?: string;
  /**
   * Optional description/supporting text
   */
  description?: string;
  /**
   * Custom icon to display. If not provided, uses default icon based on color.
   */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Callback when close button is clicked
   */
  onClose?: () => void;
  /**
   * Primary action button (e.g., "View changes")
   */
  primaryAction?: AlertAction;
  /**
   * Secondary action button (e.g., "Dismiss")
   */
  secondaryAction?: AlertAction;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the default icon based on alert color
 */
const getDefaultIcon = (
  color: AlertProps['color']
): React.ComponentType<React.SVGProps<SVGSVGElement>> => {
  switch (color) {
    case 'success':
      return CheckCircleIcon;
    case 'error':
      return XCircleIcon;
    case 'warning':
      return HelpCircleIcon;
    case 'info':
    case 'brand':
    case 'default':
    default:
      return InfoCircleIcon;
  }
};

/**
 * Get icon color class based on alert color
 */
const getIconColorClass = (color: AlertProps['color']): string => {
  switch (color) {
    case 'success':
      return 'tw:text-icon-success';
    case 'error':
      return 'tw:text-icon-error';
    case 'warning':
      return 'tw:text-icon-warning';
    case 'info':
      return 'tw:text-icon-info';
    case 'brand':
      return 'tw:text-icon-brand';
    case 'default':
    default:
      return 'tw:text-icon-primary';
  }
};

const renderCloseButton = (showCloseButton: boolean, onClose: () => void) => {
  return (
    showCloseButton &&
    onClose && (
      <button
        type="button"
        onClick={onClose}
        className="tw:shrink-0 tw:p-none tw:bg-transparent tw:border-none tw:cursor-pointer"
        aria-label="Close alert"
      >
        <Icon
          icon={CloseIcon}
          sizeClass="tw:h-l tw:w-l"
          colorClass="tw:text-icon-primary tw:hover:text-icon-primary-hover"
        />
      </button>
    )
  );
};

// ============================================
// COMPONENT
// ============================================

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      size = 'floating',
      color,
      type,
      title,
      description,
      icon,
      showCloseButton = true,
      onClose,
      primaryAction,
      secondaryAction,
      ...props
    },
    ref
  ) => {
    const IconComponent = icon || getDefaultIcon(color);
    const iconColorClass = getIconColorClass(color);

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          alertVariants({ size, color, type }),
          size === 'floating' &&
            (title ? 'tw:items-start' : 'tw:items-center'),
          className
        )}
        {...props}
      >
        {/* Content - Different layout for floating vs full-width */}
        {size === 'floating' ? (
          /* Floating: Vertical layout with actions below text */
          <>
            {/* Icon */}
            <Icon
              icon={IconComponent}
              sizeClass="tw:h-xl tw:w-xl"
              colorClass={iconColorClass}
              className="tw:shrink-0"
            />
            <div className="tw:flex tw:flex-col tw:gap-m tw:flex-1">
              {/* Text and supporting text */}
              <div className="tw:flex tw:flex-col tw:gap-2xs">
                {title ? (
                  <Typography
                    variant="h4"
                    align="left"
                    colorClass="tw:text-text-primary"
                    className="tw:flex-1"
                  >
                    {title}
                  </Typography>
                ) : null}

                {description && (
                  <Typography
                    variant="b2"
                    align="left"
                    colorClass="tw:text-text-secondary"
                  >
                    {description}
                  </Typography>
                )}
              </div>

              {/* Actions - Floating style (link buttons inline) */}
              {(primaryAction || secondaryAction) && (
                <div className="tw:flex tw:gap-m">
                  {secondaryAction && (
                    <Button
                      hierarchy="link"
                      size="medium"
                      text={secondaryAction.label}
                      onClick={secondaryAction.onClick}
                      className="tw:text-text-secondary tw:cursor-pointer"
                    />
                  )}
                  {primaryAction && (
                    <Button
                      hierarchy="link"
                      size="medium"
                      text={primaryAction.label}
                      onClick={primaryAction.onClick}
                      className="tw:text-text-link tw:cursor-pointer"
                    />
                  )}
                </div>
              )}
            </div>

            {renderCloseButton(showCloseButton, onClose)}
          </>
        ) : (
          /* Full-width: Horizontal layout with space-between (stacked on mobile) */
          <div className="tw:flex tw:flex-col tw:tablet:flex-row tw:tablet:items-center tw:tablet:justify-between tw:flex-1">
            <div className="tw:flex tw:items-start tw:tablet:items-center tw:justify-between tw:mb-2xs tw:tablet:mb-none tw:tablet:mr-l">
              {/* Icon */}
              <Icon
                icon={IconComponent}
                sizeClass="tw:h-xl tw:w-xl"
                colorClass={iconColorClass}
                className="tw:shrink-0"
              />

              <div className="tw:tablet:hidden tw:tablet:p-none">
                {renderCloseButton(showCloseButton, onClose)}
              </div>
            </div>

            {/* Text and supporting text */}
            <div className="tw:flex tw:flex-col tw:tablet:flex-row tw:tablet:items-center tw:mb-l tw:tablet:mb-none tw:gap-3xs tw:tablet:gap-xs tw:tablet:mr-m tw:flex-1">
              {title ? (
                <Typography
                  variant="h4"
                  align="left"
                  colorClass="text-text-primary"
                >
                  {title}
                </Typography>
              ) : null}

              {description && (
                <Typography
                  variant="b2"
                  align="left"
                  colorClass="text-text-secondary"
                >
                  {description}
                </Typography>
              )}
            </div>

            {/* Actions - Full-width style (stacked on mobile, inline on tablet+) */}
            {(primaryAction || secondaryAction) && (
              <div className="tw:flex tw:flex-col tw:tablet:flex-row tw:gap-m tw:shrink-0 tw:tablet:mr-m">
                {primaryAction && (
                  <Button
                    hierarchy="primary"
                    size="medium"
                    text={primaryAction.label}
                    onClick={primaryAction.onClick}
                    className="tw:cursor-pointer tw:mobile:w-full tw:tablet:min-w-max"
                  />
                )}
                {secondaryAction && (
                  <Button
                    hierarchy="secondary"
                    size="medium"
                    text={secondaryAction.label}
                    onClick={secondaryAction.onClick}
                    className="tw:cursor-pointer tw:mobile:w-full tw:tablet:min-w-max"
                  />
                )}
              </div>
            )}

            {/* Close button */}
            <div className="tw:mobile:hidden tw:tablet:flex">
              {renderCloseButton(showCloseButton, onClose)}
            </div>
          </div>
        )}
      </div>
    );
  }
);

Alert.displayName = 'Alert';

export { Alert, alertVariants };
